/**
 * Vue-specific DOM patching for handling hydration and reactivity edge cases
 * Focuses on Vue's comment markers and text node operations
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (process.server) return

  console.log('[Vue DOM Patch] Initializing Vue-specific DOM protection...')

  const isDev = process.env.NODE_ENV !== 'production'
  const isVueComment = (node: Node): boolean => {
    return (
      node.nodeType === Node.COMMENT_NODE &&
      node.textContent &&
      (node.textContent.includes('[') ||
        node.textContent.includes(']') ||
        node.textContent === '')
    )
  }

  // Track errors by component/location
  const errorMap = new Map<string, number>()

  const getNodeDescription = (node: Node | null): string => {
    if (!node) return 'null'
    if (node.nodeType === Node.TEXT_NODE) {
      return `text("${node.textContent?.substring(0, 20)}...")`
    }
    if (node.nodeType === Node.COMMENT_NODE) {
      return `comment("${node.textContent}")`
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element
      return `<${el.tagName.toLowerCase()}${el.id ? `#${el.id}` : ''}${
        el.className ? `.${el.className.split(' ').join('.')}` : ''
      }>`
    }
    return `node(type:${node.nodeType})`
  }

  const getComponentContext = (): string => {
    // Try to identify the Vue component from the stack trace
    try {
      const stack = new Error().stack || ''
      const vueComponentMatch = stack.match(/at (\w+)\.vue/g)
      if (vueComponentMatch && vueComponentMatch.length > 0) {
        return vueComponentMatch[0].replace('at ', '').replace('.vue', '')
      }
    } catch {}
    return 'unknown'
  }

  // Patch insertBefore
  const originalInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function (
    newChild: Node,
    referenceChild: Node | null,
  ): Node {
    try {
      return originalInsertBefore.call(this, newChild, referenceChild)
    } catch (error) {
      const component = getComponentContext()
      const errorKey = `${component}:${getNodeDescription(
        newChild,
      )}:${getNodeDescription(referenceChild)}`

      // Track error frequency
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      // Only log first occurrence and milestones
      if (count === 1 || count === 10 || count === 100) {
        console.warn(
          `[Vue DOM Patch] insertBefore failed (${count}x)`,
          {
            component,
            operation: 'insertBefore',
            parent: getNodeDescription(this),
            newChild: getNodeDescription(newChild),
            referenceChild: getNodeDescription(referenceChild),
            parentConnected: this.isConnected,
            // Check if we're in a content-visibility hidden subtree
            inHiddenSubtree: (() => {
              let el = this as any
              while (el && el.style) {
                const computed = window.getComputedStyle(el)
                if (
                  computed.contentVisibility === 'hidden' ||
                  computed.visibility === 'hidden' ||
                  computed.display === 'none'
                ) {
                  return true
                }
                el = el.parentElement
              }
              return false
            })(),
          },
          isDev ? error : undefined,
        )
      }

      // Intelligent fallback based on node types
      if (isVueComment(newChild)) {
        // Vue comment markers can be safely ignored
        return newChild
      }

      if (!this.isConnected) {
        // Parent is disconnected, operation would fail anyway
        return newChild
      }

      // Try appendChild as fallback for null reference
      if (!referenceChild) {
        try {
          return this.appendChild(newChild)
        } catch {
          // Silent fail
        }
      }

      return newChild
    }
  }

  // Patch removeChild
  const originalRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function (child: Node): Node {
    try {
      return originalRemoveChild.call(this, child)
    } catch (error) {
      const component = getComponentContext()
      const errorKey = `${component}:remove:${getNodeDescription(child)}`
      const count = (errorMap.get(errorKey) || 0) + 1
      errorMap.set(errorKey, count)

      if (count === 1) {
        console.warn(
          '[Vue DOM Patch] removeChild failed',
          {
            component,
            operation: 'removeChild',
            parent: getNodeDescription(this),
            child: getNodeDescription(child),
            parentConnected: this.isConnected,
          },
          isDev ? error : undefined,
        )
      }

      return child
    }
  }

  // Patch replaceChild
  const originalReplaceChild = Node.prototype.replaceChild
  Node.prototype.replaceChild = function (
    newChild: Node,
    oldChild: Node,
  ): Node {
    try {
      return originalReplaceChild.call(this, newChild, oldChild)
    } catch (error) {
      const component = getComponentContext()
      console.warn(
        '[Vue DOM Patch] replaceChild failed',
        {
          component,
          operation: 'replaceChild',
          parent: getNodeDescription(this),
          newChild: getNodeDescription(newChild),
          oldChild: getNodeDescription(oldChild),
        },
        isDev ? error : undefined,
      )

      // Try to remove old and append new as fallback
      try {
        this.removeChild(oldChild)
        this.appendChild(newChild)
      } catch {
        // Silent fail
      }

      return oldChild
    }
  }

  // Development tools
  if (isDev && process.client) {
    ;(window as any).__vueDomPatch = {
      getErrors: () => {
        const errors = Array.from(errorMap.entries())
          .map(([key, count]) => ({ error: key, count }))
          .sort((a, b) => b.count - a.count)
        console.table(errors)
        return errors
      },
      clearErrors: () => {
        errorMap.clear()
        console.log('[Vue DOM Patch] Error map cleared')
      },
      disable: () => {
        Node.prototype.insertBefore = originalInsertBefore
        Node.prototype.removeChild = originalRemoveChild
        Node.prototype.replaceChild = originalReplaceChild
        console.log('[Vue DOM Patch] Disabled')
      },
    }

    // Log summary periodically in development
    setInterval(() => {
      if (errorMap.size > 0) {
        console.debug(
          `[Vue DOM Patch] ${errorMap.size} unique errors, ${Array.from(
            errorMap.values(),
          ).reduce((a, b) => a + b, 0)} total occurrences`,
        )
      }
    }, 60000) // Every minute
  }

  // Helper to handle null reference errors
  const handleNullReferenceError = (error: any, event?: Event) => {
    if (error && error.message) {
      // Check for null reference errors on DOM methods
      if (
        error.message.includes(
          "Cannot read properties of null (reading 'insertBefore')",
        ) ||
        error.message.includes(
          "Cannot read properties of null (reading 'removeChild')",
        ) ||
        error.message.includes(
          "Cannot read properties of null (reading 'replaceChild')",
        ) ||
        error.message.includes(
          "Cannot read properties of null (reading 'appendChild')",
        )
      ) {
        if (event) event.preventDefault() // Prevent Sentry from catching it

        const component = getComponentContext()
        const errorKey = `null-reference:${error.message.match(/'(\w+)'/)?.[1] || 'unknown'}`
        const count = (errorMap.get(errorKey) || 0) + 1
        errorMap.set(errorKey, count)

        if (count === 1 || count === 10 || count === 100) {
          console.warn(
            `[Vue DOM Patch] Null reference error caught (${count}x)`,
            {
              component,
              error: error.message,
              stack: isDev ? error.stack : undefined,
            },
          )
        }
        return true
      }
    }
    return false
  }

  // Catch null reference errors from unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleNullReferenceError(event.reason, event)
  })

  // Also catch them from regular errors
  window.addEventListener(
    'error',
    (event) => {
      if (handleNullReferenceError(event.error, event)) {
        // Error was handled
        return
      }
    },
    true,
  )

  // Listen for route changes to clear error map
  nuxtApp.hook('app:mounted', () => {
    nuxtApp.$router?.afterEach(() => {
      // Clear error map on route change to track per-page issues
      if (errorMap.size > 10) {
        errorMap.clear()
      }
    })
  })

  // Verify patches are working
  const patchesApplied =
    Node.prototype.insertBefore !== originalInsertBefore &&
    Node.prototype.removeChild !== originalRemoveChild &&
    Node.prototype.replaceChild !== originalReplaceChild

  if (patchesApplied) {
    console.log(
      '[Vue DOM Patch] ✅ Vue DOM protection active - DOM operations protected against failures and null references',
    )
    console.log(
      '[Vue DOM Patch] 📊 Use window.__vueDomPatch.getErrors() to see any caught errors',
    )
  } else {
    console.error('[Vue DOM Patch] ❌ FAILED to apply some patches!')
  }
})
